import { Form, Input, Button, Flex, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { MediaProjectResponse, ProjectTag } from "pages/media/types";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { CaretLeftIcon } from "@phosphor-icons/react";
import { API_URL } from "@/constants";
import { guessProjectTitleFromUrl } from "../../utils";
import PageHeader from "components/PageHeader/";
import { InputHelperText } from "components/InputHelperText";
import { RiBardLine } from "@remixicon/react";
import { customAlphabet } from "nanoid";
import useHeyGeniusSaveMediaSettings from "../hooks/api/useHeyGeniusSaveMediaSettings";
import { useOrganization } from "hooks/useOrganization/";

interface FormValues {
  url: string;
  pageId: string;
}

export const HeyGeniusProjectCreateForm = ({
  onSubmit,
  onSuccess,
  onCancel,
}: {
  onSubmit: (url: string, tags: ProjectTag[]) => Promise<MediaProjectResponse>;
  onSuccess: (newProject: MediaProjectResponse) => void;
  onCancel: () => void;
}) => {
  const t = useTranslate();
  const [form] = Form.useForm<FormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const formValues = Form.useWatch([], form);
  const { mutateAsync: createDocument } = useCustomMutation();
  const saveMediaSettings = useHeyGeniusSaveMediaSettings();
  const { organization } = useOrganization({});
  const organizationId = organization?.id;

  // TODO: move it to generate media button and support multiple instances
  const saveHeyGeniusMediaSettings = async (
    newProject: MediaProjectResponse
  ) => {
    if (!newProject) return;
    const storyboardId = newProject?.storyboards?.[0]?.id;
    const projectId = newProject?.id;
    const instanceId = newProject?.media?.find(
      (x) => x.storyboard_settings_id === storyboardId
    )?.instance_id;
    if (!instanceId || !projectId || !organizationId) return;
    await saveMediaSettings(organizationId, projectId, instanceId);
  };

  const handleFinish = async (values: FormValues) => {
    setIsLoading(true);
    const tags = [
      {
        tag_key: "web_page_url",
        tag_value: values.url,
      },
      {
        tag_key: "page_id",
        tag_value: values.pageId,
      },
    ];

    const newProject = await onSubmit(values.url, tags).catch((error) => {
      console.error("Error saving URL:", error);
    });

    try {
      if (!newProject) {
        throw new Error("Failed to create project");
      }
      await saveHeyGeniusMediaSettings(newProject);

      // Apparently we need to call this endpoint to start the generation on the
      // the backend.
      async function createFakeDocumentToStartGeneration(
        newProject: MediaProjectResponse
      ) {
        // Apparently the backend doesn't like backslashes even if they're URL encoded
        const name = guessProjectTitleFromUrl(values.url).replace(/\//g, "");
        const document = {
          name,
          document_type: "WebPage",
          category: "WebPage",
          source_url: values.url,
          sub_category: null, // Flo says it's not relevant to HTML
        };
        await createDocument({
          url: `${API_URL}/media/projects/${newProject.id}/documents/${encodeURIComponent(name)}`,
          values: JSON.stringify(document),
          method: "post",
        });
      }
      await createFakeDocumentToStartGeneration(newProject);
      onSuccess(newProject);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePageId = () => {
    const pageId = customAlphabet("abcdefghijklmnopqrstuvwxyz", 16)();
    form.setFieldsValue({ pageId });
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsValid(true))
      .catch(() => setIsValid(false));
  }, [form, formValues]);

  return (
    <Flex vertical gap={30}>
      <PageHeader
        title={t("projects.components.HeyGeniusProjectCreateForm.title")}
        subtitle={t("projects.components.HeyGeniusProjectCreateForm.subtitle")}
      />

      <Form
        form={form}
        onFinish={(values) => {
          handleFinish(values).catch(console.error);
        }}
        disabled={isLoading}
        requiredMark={false}
        layout="vertical"
        style={{ maxWidth: 800, margin: "auto" }}
      >
        <Flex vertical gap={10}>
          <div>
            <Form.Item
              name="url"
              label={t("projects.components.HeyGeniusProjectCreateForm.url")}
              rules={[
                {
                  required: true,
                  message: t("pages.onboarding.errors.website.invalidUrl"),
                },
                {
                  type: "url",
                  message: t("pages.onboarding.errors.website.invalidUrl"),
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input size="large" placeholder="https://" />
            </Form.Item>
            <InputHelperText>
              {t("projects.components.HeyGeniusProjectCreateForm.urlHelp")}
            </InputHelperText>
          </div>

          <div>
            <Form.Item
              name="pageId"
              label={t("projects.components.HeyGeniusProjectCreateForm.pageId")}
              rules={[
                {
                  required: true,
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="large"
                placeholder="com.company.page_id"
                addonAfter={
                  <Tooltip title={t("buttons.generate")}>
                    <Button
                      onClick={generatePageId}
                      type="text"
                      icon={<RiBardLine size={20} />}
                    ></Button>
                  </Tooltip>
                }
              />
            </Form.Item>
            <InputHelperText>
              {t("projects.components.HeyGeniusProjectCreateForm.pageIdHelp")}
            </InputHelperText>
          </div>

          <Flex style={{ marginTop: 30 }} justify="space-between">
            <Button
              type="default"
              size="large"
              shape="round"
              onClick={onCancel}
              icon={<CaretLeftIcon />}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              type="primary"
              size="large"
              shape="round"
              disabled={isLoading || !isValid}
              loading={isLoading}
              htmlType="submit"
            >
              {t("buttons.create")}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Flex>
  );
};
